/**
 * Curated catalog of pages that can be saved as a quick link or set as the
 * user's homepage. Adding a route to the app does NOT automatically expose
 * it here — keep this list focused on stable, high-value destinations to
 * avoid broken links when routes change.
 */
import {
  Apple,
  Award,
  BookOpen,
  Calendar,
  ClipboardCheck,
  FileText,
  FolderKanban,
  HelpCircle,
  Home,
  IndianRupee,
  LayoutDashboard,
  Lightbulb,
  ListTodo,
  type LucideIcon,
  MessageCircle,
  Receipt,
  Settings as SettingsIcon,
  Share2,
  ShoppingBag,
  ShoppingCart,
  User,
  UsersRound,
  Watch,
} from 'lucide-react';
import {
  DEFAULT_FEATURE_FLAGS,
  FEATURE_FLAGS,
  type FeatureFlags,
  isFeatureEnabled,
  isFeatureStrictlyEnabled,
} from '@/config/feature-flags';
import { NAVIGATION_ITEM_PERMISSIONS } from '@/config/navigation-permission-registry';
import type { NavigationPermissionRequirement } from '@/types/navigation-permissions';
import type { PermissionAction, PermissionFeature } from '@/utils/permissions';

export interface QuickLinkPage {
  key: string;
  label: string;
  pathTemplate: string;
  requiredPermission: NavigationPermissionRequirement;
  group: 'member' | 'staff' | 'shared';
  icon: LucideIcon;
  featureFlag?: keyof FeatureFlags;
}

const permissionFor = (key: string): NavigationPermissionRequirement =>
  NAVIGATION_ITEM_PERMISSIONS[key];

export const QUICK_LINK_PAGES: ReadonlyArray<QuickLinkPage> = [
  {
    key: 'm-home',
    label: 'Home',
    pathTemplate: '/member/home/:clientId',
    requiredPermission: permissionFor('m-home'),
    group: 'member',
    icon: Home,
  },
  {
    key: 'm-todo',
    label: 'Membership',
    pathTemplate: '/member/todo/:clientId',
    requiredPermission: permissionFor('m-todo'),
    group: 'member',
    icon: ListTodo,
  },
  {
    key: 'm-reports',
    label: 'Reports',
    pathTemplate: '/member/reports/:clientId',
    requiredPermission: permissionFor('m-reports'),
    group: 'member',
    icon: FileText,
  },
  {
    key: 'm-marketplace',
    label: 'Marketplace',
    pathTemplate: '/member/marketplace/:clientId',
    requiredPermission: permissionFor('m-marketplace'),
    group: 'member',
    icon: ShoppingBag,
    featureFlag: FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE,
  },
  {
    key: 'm-purchases',
    label: 'My Purchases',
    pathTemplate: '/member/my-purchases/:clientId',
    requiredPermission: permissionFor('m-purchases'),
    group: 'member',
    icon: Receipt,
    featureFlag: FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE,
  },
  {
    key: 'm-benefits',
    label: 'My Benefits',
    pathTemplate: '/member/benefits/:clientId',
    requiredPermission: permissionFor('m-benefits'),
    group: 'member',
    icon: Award,
    featureFlag: FEATURE_FLAGS.ENABLE_MEMBER_MARKETPLACE,
  },
  {
    key: 'm-carts',
    label: 'My Carts',
    pathTemplate: '/member/my-carts/:clientId',
    requiredPermission: permissionFor('m-carts'),
    group: 'member',
    icon: ShoppingCart,
    featureFlag: FEATURE_FLAGS.ENABLE_MEMBER_CARTS,
  },
  {
    key: 'm-profile',
    label: 'Profile',
    pathTemplate: '/member/profile/:clientId',
    requiredPermission: permissionFor('m-profile'),
    group: 'member',
    icon: User,
  },
  {
    key: 'm-settings',
    label: 'Settings',
    pathTemplate: '/member/settings/:clientId',
    requiredPermission: permissionFor('m-settings'),
    group: 'member',
    icon: SettingsIcon,
  },
  {
    key: 'm-support',
    label: 'Support',
    pathTemplate: '/member/support/:clientId',
    requiredPermission: permissionFor('m-support'),
    group: 'member',
    icon: HelpCircle,
  },
  {
    key: 'm-chat',
    label: 'Chat',
    pathTemplate: '/chat',
    requiredPermission: permissionFor('m-chat'),
    group: 'member',
    icon: MessageCircle,
    featureFlag: FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP,
  },
  {
    key: 'm-wearables',
    label: 'Wearables',
    pathTemplate: '/member/wearables/:clientId',
    requiredPermission: permissionFor('m-wearables'),
    group: 'member',
    icon: Watch,
    featureFlag: FEATURE_FLAGS.ENABLE_WEARABLES,
  },
  {
    key: 'm-daily-log',
    label: 'My Daily Log',
    pathTemplate: '/member/daily-checkin/:clientId',
    requiredPermission: permissionFor('m-daily-log'),
    group: 'member',
    icon: ClipboardCheck,
    featureFlag: FEATURE_FLAGS.ENABLE_DAILY_LOG,
  },
  {
    key: 's-hub',
    label: 'Hub',
    pathTemplate: '/hub',
    requiredPermission: permissionFor('s-hub'),
    group: 'staff',
    icon: Share2,
  },
  {
    key: 's-business',
    label: 'Business Dashboard',
    pathTemplate: '/business-dashboard',
    requiredPermission: permissionFor('s-business'),
    group: 'staff',
    icon: IndianRupee,
  },
  {
    key: 's-appointments',
    label: 'Appointments',
    pathTemplate: '/appointments',
    requiredPermission: permissionFor('s-appointments'),
    group: 'staff',
    icon: Calendar,
  },
  {
    key: 's-tickets',
    label: 'Client Tickets',
    pathTemplate: '/client-tickets',
    requiredPermission: permissionFor('s-tickets'),
    group: 'staff',
    icon: FolderKanban,
    featureFlag: FEATURE_FLAGS.ENABLE_CLIENT_TICKETS,
  },
  {
    key: 's-people',
    label: 'People Management',
    pathTemplate: '/users',
    requiredPermission: permissionFor('s-people'),
    group: 'staff',
    icon: UsersRound,
  },
  {
    key: 's-analytics',
    label: 'Analytics',
    pathTemplate: '/dashboard',
    requiredPermission: permissionFor('s-analytics'),
    group: 'staff',
    icon: LayoutDashboard,
  },
  {
    key: 's-nutrition',
    label: 'Nutrition Planner',
    pathTemplate: '/nutrition/planner',
    requiredPermission: permissionFor('s-nutrition'),
    group: 'staff',
    icon: Apple,
  },
  {
    key: 's-knowledge',
    label: 'Knowledge Base',
    pathTemplate: '/knowledge-base',
    requiredPermission: permissionFor('s-knowledge'),
    group: 'staff',
    icon: BookOpen,
  },
  {
    key: 'm-ideas',
    label: 'Ideas',
    pathTemplate: '/ideas',
    requiredPermission: permissionFor('m-ideas'),
    group: 'member',
    icon: Lightbulb,
    featureFlag: FEATURE_FLAGS.ENABLE_MEMBER_IDEAS,
  },
  {
    key: 's-ideas',
    label: 'Ideas',
    pathTemplate: '/ideas',
    requiredPermission: permissionFor('s-ideas'),
    group: 'staff',
    icon: Lightbulb,
  },
  {
    key: 'shared-preferences',
    label: 'Preferences',
    pathTemplate: '/preferences',
    requiredPermission: permissionFor('shared-preferences'),
    group: 'shared',
    icon: SettingsIcon,
  },
];

export function findQuickLinkPage(
  pathTemplate: string,
  hasPermission?: (feature: PermissionFeature, action: PermissionAction) => boolean
): QuickLinkPage | undefined {
  const matches = QUICK_LINK_PAGES.filter((page) => page.pathTemplate === pathTemplate);
  if (matches.length === 0) return undefined;
  if (hasPermission) {
    const permitted = matches.find((page) =>
      hasPermission(page.requiredPermission.feature, page.requiredPermission.action)
    );
    if (permitted) return permitted;
  }
  return matches[0];
}

export function isQuickLinkPageAvailable(
  page: QuickLinkPage,
  flags?: FeatureFlags | null
): boolean {
  if (!page.featureFlag) return true;
  if (!flags) return true;
  if (page.featureFlag === FEATURE_FLAGS.ENABLE_IN_APP_CHAT_WEBAPP) {
    return isFeatureStrictlyEnabled(flags, page.featureFlag);
  }
  return isFeatureEnabled(flags, page.featureFlag);
}

export function listQuickLinkPagesForPermissions(
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean
): QuickLinkPage[] {
  return QUICK_LINK_PAGES.filter((page) =>
    hasPermission(page.requiredPermission.feature, page.requiredPermission.action)
  );
}

export function listAvailableQuickLinkPages(
  hasPermission: (feature: PermissionFeature, action: PermissionAction) => boolean,
  flags?: FeatureFlags | null
): QuickLinkPage[] {
  const effectiveFlags = flags ?? DEFAULT_FEATURE_FLAGS;
  return listQuickLinkPagesForPermissions(hasPermission).filter((page) =>
    isQuickLinkPageAvailable(page, effectiveFlags)
  );
}
